import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { Button, Select, Menu } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEraser, faBan, faFilePdf, faRotate, faEllipsisV, faDownload, faLaptop } from '@fortawesome/free-solid-svg-icons';
import { faWindows, faLinux } from '@fortawesome/free-brands-svg-icons';
import { useNavigate } from 'react-router-dom';

const columns = [
  {
    accessorKey: 'device',
    header: 'Device Name',
    size: 180,
    minSize: 120,
    Cell: ({ cell, row }) => {
      const deviceName = cell.getValue();
      const navigate = useNavigate();
      return (
        <div
          className="flex items-center space-x-2 cursor-pointer group"
          onClick={() => navigate(`/drive-details/${deviceName}`, { state: { device: row.original } })}
        >
          <FontAwesomeIcon icon={faLaptop} className="text-[#595C60] group-hover:text-[#FF7A00]" />
          <span className="text-[#595C60] group-hover:text-[#FF7A00]">
            {deviceName}
          </span>
        </div>
      );
    },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'department',
    header: 'Department',
    size: 180,
    minSize: 120,
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'os',
    header: 'OS',
    size: 150,
    minSize: 100,
    Cell: ({ cell }) => {
      const osName = cell.getValue();
      const isWindows = osName.toLowerCase().includes('windows');
      const icon = isWindows ? faWindows : faLinux;
      const iconColor = isWindows ? '#00B0FF' : '#202224';
      return (
        <div className="flex items-center space-x-2">
          <FontAwesomeIcon icon={icon} size="sm" style={{ color: iconColor }} />
          <span>{osName}</span>
        </div>
      );
    },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'model',
    header: 'Model',
    size: 150,
    minSize: 120,
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'location',
    header: 'Location',
    size: 150,
    minSize: 120,
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'ipAddress',
    header: 'IP Address',
    size: 150,
    minSize: 120,
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'macAddress',
    header: 'MAC Address',
    size: 150,
    minSize: 120,
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'disks',
    header: 'Disks',
    size: 120,
    minSize: 80,
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 150,
    minSize: 100,
    Cell: ({ cell }) => {
      const status = cell.getValue();
      const colors = {
        'Ready': { background: '#2ECC71', text: '#E9F9EF' },
        'In Progress': { background: '#FF7A00', text: '#FFF3E5' },
        'Completed': { background: '#3498DB', text: '#EAF4FB' },
        'Failed': { background: '#E74C3C', text: '#FCEDEC' },
      };
      const { background, text } = colors[status] || { background: '#000000', text: '#FFFFFF' };
      return (
        <span
          style={{
            backgroundColor: background,
            color: text,
            width: '94px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          {status}
        </span>
      );
    },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
      },
    },
  },
  {
    accessorKey: 'diskType',
    header: 'Disk to be erased',
    size: 150,
    minSize: 120,
    Cell: ({ cell, row }) => {
      const diskCount = row.original.diskCount || 1;
      const diskOptions = ['All Disks', ...Array.from({ length: diskCount }, (_, i) => `Disk ${i + 1}`)];
      return (
        <Select
          data={diskOptions}
          defaultValue="All Disks"
          size="xs"
          styles={{
            input: {
              fontFamily: 'Nunito Sans, sans-serif',
              color: '#595C60',
              fontWeight: 600,
              backgroundColor: 'transparent !important',
              border: '0.6px solid #202224',
              '&:focus': {
                borderColor: '#FF6B00',
                outline: 'none',
              },
            },
            wrapper: {
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'transparent !important',
            },
            item: {
              fontFamily: 'Nunito Sans, sans-serif',
              color: '#595C60',
              '&[data-selected]': {
                backgroundColor: '#FF6B00',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#FF7A00',
                  color: '#FFFFFF',
                },
              },
              '&:hover': {
                backgroundColor: '#FFF3E5',
              },
            },
          }}
        />
      );
    },
    mantineTableBodyCellProps: {
      style: {
        padding: '0 12px',
        height: '48px',
        backgroundColor: 'transparent !important',
      },
    },
  },
  {
    accessorKey: 'actions',
    header: 'Actions',
    size: 100,
    minSize: 80,
    Cell: ({ row }) => {
      const navigate = useNavigate();
      const status = row.original.status;
      let dynamicIcon;
      switch (status) {
        case 'Ready':
          dynamicIcon = faEraser;
          break;
        case 'In Progress':
          dynamicIcon = faBan;
          break;
        case 'Completed':
          dynamicIcon = faFilePdf;
          break;
        case 'Failed':
          dynamicIcon = faRotate;
          break;
        default:
          dynamicIcon = faEraser;
      }
      return (
        <div className="flex justify-start items-center space-x-3" style={{ backgroundColor: 'transparent' }}>
          <FontAwesomeIcon
            icon={faEye}
            size="lg"
            className="text-[#595C60] cursor-pointer hover:text-[#3f4245]"
            title="View"
            onClick={() => navigate(`/drive-details/${row.original.device}`, { state: { device: row.original } })}
          />
          <FontAwesomeIcon
            icon={dynamicIcon}
            size="lg"
            className="text-[#FF9F43] cursor-pointer hover:text-[#e68f3b]"
            title={status}
          />
          <Menu
            shadow="md"
            width={200}
            styles={{
              dropdown: {
                backgroundColor: '#ffffff',
              },
              item: {
                backgroundColor: 'transparent',
                color: '#595C60',
                '&[data-selected]': {
                  backgroundColor: '#FF6B00',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#FF6B00',
                    color: '#FFFFFF',
                  },
                },
                '&:hover': {
                  backgroundColor: '#FFF3E5',
                },
              },
            }}
          >
            <Menu.Target>
              <FontAwesomeIcon
                icon={faEllipsisV}
                size="lg"
                className="text-[#595C60] cursor-pointer hover:text-[#3f4245]"
                title="More"
              />
            </Menu.Target>
            <Menu.Dropdown>
              {/* <Menu.Item onClick={() => console.log('Nuke OS')}>
                Nuke OS
              </Menu.Item> */}
              <Menu.Item onClick={() => console.log('Erase w/o OS')}>
                Erase w/o OS
              </Menu.Item>
              <Menu.Item onClick={() => console.log('USB Block')}>
                USB Block
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      );
    },
    mantineTableBodyCellProps: {
      style: {
        height: '48px',
        backgroundColor: 'transparent !important',
      },
    },
  },
];

// Function to export data as CSV
const exportToCSV = (data, filename) => {
  if (!Array.isArray(data)) {
    console.warn('exportToCSV: Data is not an array, defaulting to empty array.');
    return;
  }

  const headers = [
    'Device Name',
    'Department',
    'OS',
    'Model',
    'Location',
    'IP Address',
    'MAC Address',
    'Disks',
    'Status',
    'Disk to be erased',
  ];
  const rows = data.map(item => [
    item.device,
    item.department,
    item.os,
    item.model,
    item.location,
    item.ipAddress,
    item.macAddress,
    item.disks,
    item.status,
    'All Disks',
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.click();
  URL.revokeObjectURL(url);
};

const DeviceTable = ({ data = [] }) => {
  const table = useMantineReactTable({
    columns,
    data,
    enableRowSelection: true,
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableColumnHiding: true,
    mantineTableContainerProps: {
      style: {
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: '400px',
        paddingLeft: '20px',
        paddingRight: '20px',
      },
    },
    mantineTableProps: {
      style: {
        minWidth: 'max-content',
        tableLayout: 'fixed',
        fontFamily: 'Nunito Sans, sans-serif',
        backgroundColor: '#ffffff',
        width: '100%',
      },
      className: 'shadow-sm rounded-lg',
    },
    mantineTableHeadCellProps: {
      style: {
        whiteSpace: 'nowrap',
        backgroundColor: '#F1F4F9',
        borderBottom: '1px solid #e5e7eb',
        padding: '12px',
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: '700',
        fontSize: '14px',
        color: '#202224',
      },
    },
    mantineTableBodyRowProps: {
      style: {
        borderBottom: '0.5px solid #979797',
        backgroundColor: '#ffffff',
        '&:hover': {
          backgroundColor: '#f5f5f5 !important',
        },
      },
    },
    mantineTableBodyCellProps: {
      style: {
        whiteSpace: 'nowrap',
        padding: '12px',
        height: '48px',
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: '600',
        color: '#595C60',
        backgroundColor: 'transparent !important',
        '&:hover': {
          backgroundColor: 'transparent !important',
        },
      },
    },
    mantineSelectCheckboxProps: {
      size: 'sm',
      styles: {
        input: {
          '&:not(:checked)': {
            border: '1px solid #202224 !important',
            backgroundColor: 'transparent !important',
          },
          '&:checked': {
            border: 'none !important',
            backgroundColor: '#FF7A00 !important',
          },
        },
        icon: {
          color: '#ffffff !important',
        },
        wrapper: {
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'transparent !important',
        },
      },
      className: 'custom-checkbox-cell',
    },
    mantineSelectAllCheckboxProps: {
      size: 'sm',
      styles: {
        input: {
          '&:not(:checked)': {
            border: '1px solid #202224 !important',
          },
          '&:checked': {
            border: 'none !important',
            backgroundColor: '#FF7A00 !important',
          },
        },
        icon: {
          color: '#ffffff !important',
        },
        wrapper: {
          borderBottom: '1px solid #e5e7eb',
        },
      },
    },
    mantineColumnVisibilityToggleButtonProps: {
      sx: {
        color: '#FF7A00 !important',
        fontFamily: 'Nunito Sans, sans-serif',
        fontSize: '14px',
        border: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: '#FFF3E5',
        },
        '& svg': {
          color: '#FF7A00 !important',
        },
      },
    },
    mantineTableToolbarProps: {
      sx: {
        '& .mantine-Menu-item': {
          color: '#FF7A00 !important',
          fontFamily: 'Nunito Sans, sans-serif',
          fontSize: '14px',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: '#FFF3E5 !important',
          },
        },
        '& .mantine-Menu-item[data-testid="mrt-column-visibility-show-all-button"], & .mantine-Menu-item[data-testid="mrt-column-visibility-hide-all-button"]': {
          color: '#FF7A00 !important',
          '&:hover': {
            backgroundColor: '#FFF3E5 !important',
          },
        },
      },
    },
  });

  return (
    <div className="mb-5">
      <style>
        {`
          .custom-checkbox-cell,
          .custom-checkbox-cell * {
            background-color: transparent !important;
          }
          .custom-checkbox-cell:hover,
          .custom-checkbox-cell *:hover {
            background-color: transparent !important;
          }
          .mantine-Menu-item[data-testid="mrt-column-visibility-show-all-button"],
          .mantine-Menu-item[data-testid="mrt-column-visibility-hide-all-button"] {
            color: #FF7A00 !important;
            font-family: 'Nunito Sans, sans-serif' !important;
            font-size: 14px !important;
          }
          .mantine-Menu-item[data-testid="mrt-column-visibility-show-all-button"]:hover,
          .mantine-Menu-item[data-testid="mrt-column-visibility-hide-all-button"]:hover {
            background-color: #FFF3E5 !important;
          }
          .export-button {
            position: relative;
            overflow: hidden;
          }
          .export-button-ripple {
            position: absolute;
            border-radius: 50%;
            background-color: rgba(255, 255, 255, 0.4);
            transform: scale(0);
            animation: ripple 0.6s linear;
            pointer-events: none;
          }
          @keyframes ripple {
            to {
              transform: scale(4);
              opacity: 0;
            }
          }
        `}
      </style>
      <div className="flex justify-between items-center mb-4">
        <h2 style={{ fontFamily: 'Nunito Sans, sans-serif', fontSize: '24px', fontWeight: 700, color: '#202224' }}>
          Devices
        </h2>
        <div className="flex items-center">
          <Button
            size="sm"
            variant="filled"
            className="export-button"
            styles={{
              root: {
                width: '120px',
                height: '36px',
                backgroundColor: '#FF7A00',
                borderRadius: '20px',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                '&:hover': {
                  backgroundColor: '#FF7A00',
                },
              },
              label: {
                fontFamily: 'Nunito Sans, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              },
            }}
            onClick={(e) => {
              const button = e.currentTarget;
              const ripple = document.createElement('span');
              const diameter = Math.max(button.clientWidth, button.clientHeight);
              const radius = diameter / 2;

              ripple.style.width = ripple.style.height = `${diameter}px`;
              ripple.style.left = `${e.clientX - button.getBoundingClientRect().left - radius}px`;
              ripple.style.top = `${e.clientY - button.getBoundingClientRect().top - radius}px`;
              ripple.classList.add('export-button-ripple');

              button.appendChild(ripple);

              setTimeout(() => {
                ripple.remove();
              }, 600);

              exportToCSV(data, 'devices.csv');
            }}
          >
            <FontAwesomeIcon
              icon={faDownload}
              style={{
                height: '10px',
                width: '10px',
                color: '#fff',
              }}
            />
            <span>Export</span>
          </Button>
          <div style={{ width: '30px' }} />
          <Select
            placeholder="Bulk Actions"
            data={['Nuke Windows', 'Export Selected', 'Wipe w/o OS', 'USB Block']}
            size="sm"
            styles={{
              input: {
                fontFamily: 'Nunito Sans, sans-serif',
                fontSize: '14px',
                backgroundColor: '#f9fafb',
                borderColor: '#202224',
                borderWidth: '0.6px',
                color: '#202224',
                borderRadius: '6px',
                padding: '0 12px',
                height: '36px',
                width: '150px',
                '&:hover': {
                  backgroundColor: '#e5e7eb',
                },
                '&:active': {
                  backgroundColor: '#d1d5db',
                },
                '&:focus': {
                  borderColor: '#FF6B00',
                  outline: 'none',
                },
                '&::placeholder': {
                  color: '#202224',
                },
              },
              dropdown: {
                fontFamily: 'Nunito Sans, sans-serif',
                color: '#202224',
                backgroundColor: '#ffffff',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
              },
              item: {
                fontFamily: 'Nunito Sans, sans-serif',
                fontSize: '14px',
                color: '#202224',
                padding: '8px 12px',
                '&[data-selected]': {
                  backgroundColor: '#FF6B00',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#FF6B00',
                    color: '#FFFFFF',
                  },
                },
                '&:hover': {
                  backgroundColor: '#FFF3E5',
                },
              },
            }}
            onChange={(value) => {
              if (value === 'Export Selected') {
                const selectedRows = table.getSelectedRowModel()?.rows || [];
                if (selectedRows.length > 0) {
                  exportToCSV(selectedRows.map(row => row.original), 'selected-devices.csv');
                }
              }
            }}
          />
        </div>
      </div>
      <MantineReactTable table={table} />
    </div>
  );
};

export default DeviceTable;